import { adminFetch } from "./_client";
import type { SeoPageMetadata, ProgrammaticGeoClusterId, ProgrammaticGeoCluster, ProgrammaticGeoPage, ProgrammaticGeoGenerateResponse, SitemapPreview, SearchAnalyticsResponse, IndexingLogResponse } from "./types";

export function fetchSeoPages(token: string, page = 1) {
  return adminFetch<{ pages: SeoPageMetadata[]; total: number; page: number; limit: number }>(`/admin/seo/pages?page=${page}`, token);
}


export function createSeoPage(token: string, data: Partial<SeoPageMetadata>) {
  return adminFetch<{ success: boolean; page: SeoPageMetadata }>("/admin/seo/pages", token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}


export function updateSeoPage(token: string, id: number, data: Partial<SeoPageMetadata>) {
  return adminFetch<{ success: boolean }>(`/admin/seo/pages/${id}`, token, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}


export function deleteSeoPage(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/seo/pages/${id}`, token, {
    method: "DELETE",
  });
}


export function fetchRobotsTxt(token: string) {
  return adminFetch<{ content: string; isCustom: boolean }>("/admin/seo/robots-txt", token);
}


export function fetchProgrammaticGeoClusters(token: string) {
  return adminFetch<{ clusters: ProgrammaticGeoCluster[] }>("/admin/programmatic-geo/clusters", token);
}


export function fetchProgrammaticGeoPages(token: string, params: { cluster?: string; market?: string; limit?: number } = {}) {
  const sp = new URLSearchParams();
  if (params.cluster) sp.set("cluster", params.cluster);
  if (params.market) sp.set("market", params.market);
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return adminFetch<{ pages: ProgrammaticGeoPage[] }>(`/admin/programmatic-geo/pages${qs ? `?${qs}` : ""}`, token);
}


export function generateProgrammaticGeo(token: string, body: {
  cluster: ProgrammaticGeoClusterId;
  market: string;
  language?: string;
  combos: Array<Record<string, unknown>>;
  dryRun?: boolean;
}) {
  return adminFetch<ProgrammaticGeoGenerateResponse>("/admin/programmatic-geo/generate", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


export function refreshProgrammaticGeoSnapshot(token: string, id: number) {
  return adminFetch<{ ok: true; qualityPassed: boolean; snapshot: ProgrammaticGeoPage["quality_snapshot"] }>(
    `/admin/programmatic-geo/refresh-snapshot/${id}`, token, { method: "POST" },
  );
}


export function retireProgrammaticGeoPage(token: string, id: number, redirectTo?: string) {
  return adminFetch<{ ok: true; redirectTo: string | null }>(
    `/admin/programmatic-geo/retire/${id}`,
    token,
    { method: "POST", body: JSON.stringify(redirectTo ? { redirectTo } : {}) },
  );
}


// Cluster kill-switch + gate-override. The list endpoint
// already returns `enabled` / `gateOverride` on each cluster; this
// helper writes them back. Used by the admin tab so ops can pause a
// cluster or tighten/relax its quality gate without a deploy.
export function updateProgrammaticGeoClusterSettings(
  token: string,
  cluster: ProgrammaticGeoClusterId,
  body: { enabled?: boolean; gateOverride?: Record<string, number> | null },
) {
  return adminFetch<{ ok: true }>(
    `/admin/programmatic-geo/clusters/${cluster}/settings`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}


// Per-row override. editorialOverride=true force-publishes
// the row regardless of inventory; demandSignal is the relaxed-floor
// admission path (monthly search volume ≥ 100).
export function updateProgrammaticGeoOverride(
  token: string,
  id: number,
  body: { editorialOverride?: boolean; demandSignal?: number | null },
) {
  return adminFetch<{ ok: true }>(
    `/admin/programmatic-geo/override/${id}`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}


export function updateRobotsTxt(token: string, content: string) {
  return adminFetch<{ success: boolean }>("/admin/seo/robots-txt", token, {
    method: "PUT",
    body: JSON.stringify({ content }),
  });
}


export function triggerSitemapRegeneration(token: string) {
  return adminFetch<{ success: boolean; message: string }>("/admin/seo/sitemap/regenerate", token, {
    method: "POST",
  });
}


export function fetchSitemapPreview(token: string) {
  return adminFetch<SitemapPreview>("/admin/seo/sitemap/preview", token);
}


// Google Search Console performance: clicks/impressions/CTR/position over a
// date range plus top queries/pages. `days` is one of 7 / 28 / 90.
export function fetchSearchAnalytics(token: string, days = 28) {
  return adminFetch<SearchAnalyticsResponse>(`/admin/seo/google/search-analytics?days=${days}`, token);
}


// Google Indexing submission log: recent per-listing Indexing API outcomes plus
// aggregate success/skip/failure/quota counts (Task #4517).
export function fetchIndexingLog(token: string) {
  return adminFetch<IndexingLogResponse>("/admin/seo/google/indexing-log", token);
}
